/**
 * Helpers to derive `HowTo` structured data (schema.org JSON-LD) from
 * step-by-step content.
 *
 * A `HowTo` is only emitted when the content actually contains a step plan,
 * derived from either:
 *   1. Numbered / "Stap N" headings, or
 *   2. A numbered (ordered) list.
 *
 * When no step plan can be found, the builders return `null` so callers can
 * skip emitting the JSON-LD entirely.
 */

// A heading that introduces a step, e.g. "Stap 1", "Stap 1: ...", "1. ...",
// "2) ...". A bare leading number without punctuation (e.g. "3 tips") is
// intentionally NOT treated as a step to avoid false positives.
const STEP_HEADING_REGEX = /^\s*(stap\s+\d+|\d+\s*[.):\-–])/i

const MIN_STEPS = 2

function collapseWhitespace(text) {
  return (text || "").replace(/\s+/g, " ").trim()
}

/* ------------------------------------------------------------------ *
 * Rich text (Contentful) -> blocks
 * ------------------------------------------------------------------ */

function richTextNodeToPlainText(node) {
  if (!node) return ""
  if (node.nodeType === "text") return node.value || ""
  if (Array.isArray(node.content)) {
    return node.content.map(richTextNodeToPlainText).join("")
  }
  return ""
}

function richTextToBlocks(raw) {
  let document
  try {
    document = typeof raw === "string" ? JSON.parse(raw) : raw
  } catch {
    return []
  }
  if (!document || !Array.isArray(document.content)) return []

  const blocks = []
  for (const node of document.content) {
    if (!node || typeof node.nodeType !== "string") continue

    const headingMatch = node.nodeType.match(/^heading-(\d)$/)
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: Number(headingMatch[1]),
        text: collapseWhitespace(richTextNodeToPlainText(node)),
      })
      continue
    }

    if (node.nodeType === "ordered-list") {
      const items = (node.content || [])
        .map((item) => collapseWhitespace(richTextNodeToPlainText(item)))
        .filter(Boolean)
      blocks.push({ type: "ordered-list", items })
      continue
    }

    blocks.push({
      type: "text",
      text: collapseWhitespace(richTextNodeToPlainText(node)),
    })
  }
  return blocks
}

/* ------------------------------------------------------------------ *
 * Markdown -> blocks
 * ------------------------------------------------------------------ */

function markdownToBlocks(markdown) {
  if (!markdown || typeof markdown !== "string") return []

  const blocks = []
  let pendingListItems = null

  const flushList = () => {
    if (pendingListItems) {
      blocks.push({ type: "ordered-list", items: pendingListItems })
      pendingListItems = null
    }
  }

  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trim()
    if (line === "") {
      flushList()
      continue
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/)
    if (headingMatch) {
      flushList()
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: collapseWhitespace(headingMatch[2]),
      })
      continue
    }

    const listMatch = line.match(/^\d+\s*[.)]\s+(.*)$/)
    if (listMatch) {
      const text = collapseWhitespace(listMatch[1])
      if (text) {
        if (!pendingListItems) pendingListItems = []
        pendingListItems.push(text)
      }
      continue
    }

    flushList()
    blocks.push({ type: "text", text: collapseWhitespace(line) })
  }
  flushList()
  return blocks
}

/* ------------------------------------------------------------------ *
 * blocks -> steps
 * ------------------------------------------------------------------ */

function stepsFromHeadings(blocks) {
  const steps = []
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    if (block.type !== "heading" || !STEP_HEADING_REGEX.test(block.text)) {
      continue
    }

    // Gather the body text of this step: everything up to the next heading.
    const textParts = []
    for (let j = i + 1; j < blocks.length; j++) {
      const next = blocks[j]
      if (next.type === "heading") break
      if (next.type === "text" && next.text) textParts.push(next.text)
      if (next.type === "ordered-list" && next.items.length) {
        textParts.push(next.items.join(" "))
      }
    }

    const step = { "@type": "HowToStep", name: block.text }
    const text = collapseWhitespace(textParts.join(" "))
    if (text) step.text = text
    steps.push(step)
  }
  return steps
}

function stepsFromOrderedList(blocks) {
  // Use the largest ordered list found in the content.
  let best = null
  for (const block of blocks) {
    if (block.type !== "ordered-list") continue
    if (!best || block.items.length > best.items.length) best = block
  }
  if (!best) return []

  return best.items.map((item, index) => ({
    "@type": "HowToStep",
    name: `Stap ${index + 1}`,
    text: item,
  }))
}

function stepsFromBlocks(blocks) {
  const headingSteps = stepsFromHeadings(blocks)
  if (headingSteps.length >= MIN_STEPS) return headingSteps

  const listSteps = stepsFromOrderedList(blocks)
  if (listSteps.length >= MIN_STEPS) return listSteps

  return []
}

/* ------------------------------------------------------------------ *
 * Public builders
 * ------------------------------------------------------------------ */

function buildHowTo(blocks, { name, description, url } = {}) {
  const steps = stepsFromBlocks(blocks)
  if (steps.length < MIN_STEPS) return null

  const howTo = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: collapseWhitespace(name) || undefined,
    step: steps,
  }
  const cleanDescription = collapseWhitespace(description)
  if (cleanDescription) howTo.description = cleanDescription
  if (url) howTo.url = url

  if (!howTo.name) delete howTo.name
  return howTo
}

/**
 * Build a `HowTo` object from a Contentful rich text `raw` string.
 * Returns `null` when the content contains no step plan.
 */
function buildHowToFromRichText(raw, options = {}) {
  return buildHowTo(richTextToBlocks(raw), options)
}

/**
 * Build a `HowTo` object from a markdown string.
 * Returns `null` when the content contains no step plan.
 */
function buildHowToFromMarkdown(markdown, options = {}) {
  return buildHowTo(markdownToBlocks(markdown), options)
}

module.exports = {
  buildHowToFromRichText,
  buildHowToFromMarkdown,
  // Exported for testing.
  richTextToBlocks,
  markdownToBlocks,
  stepsFromBlocks,
}
